import { useState } from "react";
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  Settings,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const MobileBottomNav = () => {
  const history = useHistory();
  const location = useLocation();
  const { permissions } = useAuth();
  const [value, setValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReportsPermission = permissions.includes('Read Reports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  const menuItems = {
    master: [
      { label: 'Company', route: '/CompanyEdit' },
      { label: 'Client', route: '/clientIndex' },
      { label: 'Vendor', route: '/vendorIndex' },
      { label: 'Account Head', route: '/accountHeadIndex' },
      { label: 'Bank', route: '/bankIndex' },
      { label: 'Category', route: '/categoryIndex' },
      { label: 'Item', route: '/itemIndex' },
    ],
    event: [
      { label: 'Product Enquiry', route: '/EnquiryIndex' },
      { label: 'Rental Enquiry', route: '/EventItemEnquiryIndex' },
      { label: 'Estimate', route: '/EstimateIndex' },
      { label: 'Delivery-Out', route: '/DeliveryIndex' },
      { label: 'Delivery-In', route: '/DeliveryInIndex' },
      { label: 'Rent Invoice', route: '/RentInvoiceIndex' },
    ],
    transaction: [
      { label: 'Purchase', route: '/PurchaseIndex' },
      { label: 'Sales', route: '/SaleIndex' },
    ],
    accounts: [
      { label: 'Payment Receipts', route: '/PaymentReceiptsIndex' },
      { label: 'Journal Entry', route: '/JournalEntryIndex' },
    ],
    reports: [
      { label: 'Stock', route: '/StockReportIndex' },
      { label: 'Item Forecast Report', route: '/ItemForecastReport' },
      { label: 'Estimate Report', route: '/MonthlyEstimateReport' },
      { label: 'Delivery Report', route: '/DeliverySummaryReport' },
      { label: 'Delivery Stock', route: '/DeliveryStockReport' },
      { label: 'Purchase Party & ItemWise Report', route: '/PurchasePartyandItemWiseReport' },
      { label: 'Sale Party & ItemWise Report', route: '/SalePartyandItemWiseReport' },
      { label: 'Rent Invoice Party & ItemWise Report', route: '/RentInvoicePartyandItemWiseReport' },
      { label: 'Ledger', route: '/LedgerReport' },
      { label: 'CashBook', route: '/CashBook' },
      { label: 'Customer Due List', route: '/CustomerDueListReport' },
      { label: 'Vendor Due List', route: '/VendorDueListReport' },
    ],
    settings: [
      { label: 'Users', route: '/UserIndex' },
      { label: 'Roles', route: '/RoleIndex' },
      { label: 'Reset Password', route: '/PasswordChange' },
    ],
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          borderTop: '1px solid rgba(0,0,0,0.1)',
        }}
        elevation={8}
      >
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => setValue(newValue)}
          sx={{
            backgroundColor: '#2c3e6e',
            height: '60px',
            '& .MuiBottomNavigationAction-root': {
              color: 'rgba(255,255,255,0.6)',
              minWidth: '50px',
              padding: '6px 0',
              '&.Mui-selected': {
                color: '#fff',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '10px',
              fontWeight: 500,
              marginTop: '2px',
            },
          }}
        >
          <BottomNavigationAction
            label="Home"
            icon={<Dashboard sx={{ fontSize: 22 }} />}
            onClick={() => history.push('/Home')}
          />
          {hasReadMasterPermission && (
            <BottomNavigationAction
              label="Master"
              icon={<Business sx={{ fontSize: 22 }} />}
              onClick={(e) => handleMenuOpen(e, 'master')}
            />
          )}
          {(hasReadTransactionPermission || hasReadMasterPermission) && (
            <BottomNavigationAction
              label="Event"
              icon={<People sx={{ fontSize: 22 }} />}
              onClick={(e) => handleMenuOpen(e, 'event')}
            />
          )}
          {(hasReadTransactionPermission || hasReadMasterPermission) && (
            <BottomNavigationAction
              label="Transaction"
              icon={<ShoppingCart sx={{ fontSize: 22 }} />}
              onClick={(e) => handleMenuOpen(e, 'transaction')}
            />
          )}
          {hasReadAccountsPermission && (
            <BottomNavigationAction
              label="Accounts"
              icon={<AccountBalance sx={{ fontSize: 22 }} />}
              onClick={(e) => handleMenuOpen(e, 'accounts')}
            />
          )}
          {hasReadReportsPermission && (
            <BottomNavigationAction
              label="Reports"
              icon={<BarChart sx={{ fontSize: 22 }} />}
              onClick={(e) => handleMenuOpen(e, 'reports')}
            />
          )}
          <BottomNavigationAction
            label="More"
            icon={<Settings sx={{ fontSize: 22 }} />}
            onClick={(e) => handleMenuOpen(e, 'settings')}
          />
        </BottomNavigation>
      </Paper>

      {/* Master Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'master'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '180px',
            mb: 1,
          }
        }}
      >
        {menuItems.master.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Event Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'event'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '180px',
            mb: 1,
          }
        }}
      >
        {menuItems.event.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Transaction Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'transaction'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '180px',
            mb: 1,
          }
        }}
      >
        {menuItems.transaction.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Accounts Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'accounts'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '180px',
            mb: 1,
          }
        }}
      >
        {menuItems.accounts.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Reports Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'reports'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '200px',
            mb: 1,
          }
        }}
      >
        {menuItems.reports.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={activeMenu === 'settings'}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            backgroundColor: '#2c3e6e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '180px',
            mb: 1,
          }
        }}
      >
        {menuItems.settings.map((item, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(item.route)}
            sx={{ fontSize: '14px', py: 1.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MobileBottomNav;
